/* Sable & Ochre - consent-gated analytics.
 *
 * WHY THIS EXISTS: the store had no analytics of any kind. No Google Analytics,
 * no pixel, nothing - so there was no record of how anyone reached the site or
 * where they dropped out, and no way to read the result of any change made to it.
 *
 * WHY IT DOES NOT DRAW A BANNER: Shopify's own privacy banner is already enabled
 * on this store and offers Accept, Decline and granular Manage preferences. A
 * second banner of ours appeared stacked on top of it and swallowed its clicks.
 * Shopify owns the UI; this file owns only the question "may I load a tracker
 * yet, and for which purpose".
 *
 * CONSENT IS ASKED FOR, NEVER ASSUMED. Nothing here fires on page load. Every
 * vendor waits for Shopify's Customer Privacy API to say yes, and the two
 * purposes are kept apart: GA4 and Plausible are analytics, the Meta pixel is
 * marketing. Someone who accepts analytics and declines marketing gets exactly
 * that.
 *
 * If the API never loads, nothing loads either. Failing closed is the only safe
 * direction: the cost of a missing pageview is a missing pageview, and the cost
 * of a tracker firing without consent is a regulator.
 */
(function () {
  'use strict';

  var cfg = readConfig();
  if (!cfg.ga4 && !cfg.plausible && !cfg.meta) return;   /* nothing configured */

  function readConfig() {
    var el = document.getElementById('hp-analytics-config');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  }

  /* ── Vendor loaders ─────────────────────────────────────────────────────
     Each is idempotent: consent can be granted, changed in Manage preferences
     and granted again within one page view, and this must not stack scripts. */

  var loaded = {};

  function inject(src, attrs) {
    var s = document.createElement('script');
    s.src = src;
    s.async = true;
    if (attrs) {
      for (var k in attrs) {
        if (Object.prototype.hasOwnProperty.call(attrs, k)) { s.setAttribute(k, attrs[k]); }
      }
    }
    document.head.appendChild(s);
  }

  function loadGa4(id) {
    if (loaded.ga4 || !id) return;
    loaded.ga4 = true;
    inject('https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(id));
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = window.gtag || gtag;
    gtag('js', new Date());
    /* We only reach here having been granted analytics consent, but GA4's own
       consent mode should be told rather than left to assume. ad_storage stays
       denied unless marketing was separately granted - see apply(). */
    gtag('consent', 'default', { analytics_storage: 'granted', ad_storage: 'denied' });
    gtag('config', id, { anonymize_ip: true });
  }

  function loadPlausible(domain) {
    if (loaded.plausible || !domain) return;
    loaded.plausible = true;
    /* Cookieless, so it would be defensible without consent - but it is gated
       anyway. One rule for every vendor is easier to reason about than a list
       of exceptions, and the shop loses nothing by asking. */
    inject('https://plausible.io/js/script.js', { 'data-domain': domain, defer: 'defer' });
  }

  function loadMeta(id) {
    if (loaded.meta || !id) return;
    loaded.meta = true;
    /* Meta's standard snippet, minus its <noscript> pixel: an image beacon that
       fires without JavaScript also fires without consent, which is the one
       thing this file exists to prevent. */
    /* eslint-disable */
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
    (window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    window.fbq('init', id);
    window.fbq('track', 'PageView');
  }

  /* ── Consent ────────────────────────────────────────────────────────────── */

  function apply(api) {
    if (!api) return;

    var analytics = false, marketing = false;
    try {
      analytics = api.analyticsProcessingAllowed();
      marketing = typeof api.marketingAllowed === 'function' ? api.marketingAllowed() : false;
    } catch (e) { return; }

    if (analytics) {
      loadGa4(cfg.ga4);
      loadPlausible(cfg.plausible);
    }
    if (marketing) {
      loadMeta(cfg.meta);
      /* If GA4 is already running under analytics consent, upgrade it rather
         than reloading it. */
      if (loaded.ga4 && typeof window.gtag === 'function') {
        window.gtag('consent', 'update', { ad_storage: 'granted' });
      }
    }
  }

  /* window.Shopify.customerPrivacy appears BEFORE its methods do.
     Caught in testing: the object was truthy while analyticsProcessingAllowed
     was still undefined, so an existence check on the object alone read as
     "API ready", threw, and this file quietly gave up loading anything for the
     rest of the page. Readiness therefore means the method exists, not the
     namespace. */
  function ready(cp) {
    return !!cp && typeof cp.analyticsProcessingAllowed === 'function';
  }

  function withPrivacyApi(cb) {
    var sh = window.Shopify;
    if (ready(sh && sh.customerPrivacy)) { cb(sh.customerPrivacy); return; }
    if (!sh || typeof sh.loadFeatures !== 'function') { cb(null); return; }

    sh.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!err && ready(cp)) { cb(cp); return; }

      /* loadFeatures has called back but the methods are not attached yet.
         Poll briefly rather than give up: this is the difference between a
         returning visitor's existing consent being honoured on load and being
         ignored until they happen to change it. Bounded, then abandoned. */
      var tries = 0;
      var t = setInterval(function () {
        var c = window.Shopify && window.Shopify.customerPrivacy;
        if (ready(c)) { clearInterval(t); cb(c); return; }
        if (++tries > 20) { clearInterval(t); cb(null); }   /* ~5s */
      }, 250);
    });
  }

  function init() {
    /* Consent given on an earlier visit, or already in this session. */
    withPrivacyApi(apply);

    /* And the moment it is given or changed. Shopify dispatches this on the
       document whenever the visitor answers the banner or edits their
       preferences, so a tracker starts as soon as it is allowed to - without a
       reload, and without us polling. */
    document.addEventListener('visitorConsentCollected', function () {
      withPrivacyApi(apply);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }
}());
